import React from "react";
import { generatedScenarioTraceabilityMatrix } from "../data/generatedScenarioTraceabilityMatrix.js";
import { generatedSpaceEnvironmentEvidenceLedger } from "../data/generatedSpaceEnvironmentEvidenceLedger.js";
import { bodies } from "../data/snapshots.js";
import { CellStack, DataTable, EvidencePanel, Metric, PageHeader, PillSelector, StatusPill } from "../components/ui.jsx";

const BODY_SCENARIO_IDS = {
  mercury: ["mercury-orbit", "mercury-polar"],
  moon: ["moon-first", "hybrid"],
  asteroids: ["asteroid-first", "hybrid"],
  mars: ["mars-first"],
  venus: [],
};

export function BodyLedger({ selectedBody, selectedBodyId, setSelectedBodyId }) {
  const linkedScenarioIds = BODY_SCENARIO_IDS[selectedBody.id] ?? [];
  const linkedScenarioTraces = generatedScenarioTraceabilityMatrix.traces.filter((trace) =>
    linkedScenarioIds.includes(trace.scenarioId),
  );
  const environmentStressRows = generatedSpaceEnvironmentEvidenceLedger.records.flatMap((record) =>
    record.scenarioStress
      .filter((stress) => linkedScenarioIds.includes(stress.scenarioId))
      .map((stress) => ({
        ...stress,
        recordId: record.id,
        evidenceLabel: record.label,
        promotionState: record.promotionGate.state,
        evidenceHash: record.evidenceHash,
      })),
  );

  return (
    <section className="module">
      <PageHeader
        title="BODY LEDGER"
        muted="Resource evidence"
        subtitle="Keep assumptions explicit and confidence tags attached to every claim."
      />
      <PillSelector items={bodies} selectedId={selectedBodyId} onSelect={setSelectedBodyId} />

      <div className="module-grid">
        <EvidencePanel title={selectedBody.name} status="Body profile" tone="blue" className="panel-large">
          <p>{selectedBody.summary}</p>
          <div className="planet-badge">
            <span>{selectedBody.name.slice(0, 2).toUpperCase()}</span>
          </div>
          <div className="metric-strip">
            {selectedBody.metrics.map(([label, value]) => (
              <Metric key={label} label={label} value={value} />
            ))}
          </div>
        </EvidencePanel>

        <EvidencePanel title="Resource ledger" status="confidence labeled" tone="green">
          <DataTable
            columns={[
              { key: "asset", header: "Asset", render: ([asset]) => asset },
              { key: "confidence", header: "Confidence", render: ([, confidence]) => confidence },
              { key: "basis", header: "Basis", render: ([, , note]) => note },
              {
                key: "useCases",
                header: "Use cases",
                render: ([asset]) => (asset.includes("Solar") ? "Power / thermal systems" : "Manufacturing and in-situ tests"),
              },
              {
                key: "verification",
                header: "Verification needed",
                render: ([, confidence]) => (confidence === "Verified" ? "None" : "Source-level confirmation"),
              },
            ]}
            rows={selectedBody.resources}
            getRowKey={([asset], idx) => `${selectedBody.id}-${asset}-${idx}`}
          />
        </EvidencePanel>
      </div>

      <div className="double-grid">
        <EvidencePanel title="Constraints & risks" status="hard blockers" tone="red">
          <DataTable
            compact
            rows={selectedBody.resources}
            getRowKey={([asset], idx) => `${selectedBody.id}-constraint-${asset}-${idx}`}
            columns={[
              {
                key: "resource",
                header: "Resource",
                render: ([asset, confidence]) => <CellStack title={asset} meta={confidence} />,
              },
              {
                key: "promotionState",
                header: "Promotion state",
                render: ([, confidence]) => (
                  <StatusPill tone={confidence === "Verified" ? "green" : confidence === "Hypothesis" ? "red" : "amber"}>
                    {confidence === "Verified" ? "citable-context" : "source-proof-required"}
                  </StatusPill>
                ),
              },
              { key: "note", header: "Constraint / risk", render: ([, , note]) => note },
            ]}
          />
        </EvidencePanel>
        <EvidencePanel title="Scenario scoring meaning" status="interpretation" tone="blue">
          <Metric label="Linked scenario traces" value={linkedScenarioTraces.length} />
          <Metric label="Blocked linked traces" value={linkedScenarioTraces.filter((trace) => !trace.commercializationAllowed).length} />
          <Metric label="Environment stress rows" value={environmentStressRows.length} />
          <Metric label="Trace matrix hash" value={generatedScenarioTraceabilityMatrix.evidenceHashes.scenarioDecisionLedger.slice(0, 18)} />
          <p>{generatedScenarioTraceabilityMatrix.operatingRule}</p>
        </EvidencePanel>
      </div>

      <EvidencePanel title="Scenario traceability links" status={generatedScenarioTraceabilityMatrix.mode} tone="red" className="panel-large">
        <DataTable
          compact
          rows={linkedScenarioTraces}
          getRowKey={(trace) => trace.scenarioId}
          columns={[
            {
              key: "scenario",
              header: "Scenario",
              render: (trace) => <CellStack title={trace.name} meta={trace.scenarioId} />,
            },
            { key: "rank", header: "Rank" },
            { key: "decisionLane", header: "Decision lane" },
            {
              key: "promotionState",
              header: "Promotion",
              render: (trace) => <StatusPill tone="red">{trace.promotionState}</StatusPill>,
            },
            {
              key: "blockers",
              header: "Blockers",
              render: (trace) => trace.blockers.length,
            },
            {
              key: "traceHash",
              header: "Trace hash",
              render: (trace) => <code>{trace.traceHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>

      <EvidencePanel title="Environment stress evidence" status={generatedSpaceEnvironmentEvidenceLedger.mode} tone="amber" className="panel-large">
        <p>{generatedSpaceEnvironmentEvidenceLedger.operatingRule}</p>
        <DataTable
          compact
          rows={environmentStressRows}
          getRowKey={(row) => `${row.recordId}-${row.scenarioId}`}
          columns={[
            {
              key: "evidence",
              header: "Evidence record",
              render: (row) => <CellStack title={row.evidenceLabel} meta={row.recordId} />,
            },
            { key: "scenarioName", header: "Scenario" },
            { key: "environmentBurden", header: "Environment burden" },
            { key: "commsBurden", header: "Comms" },
            { key: "thermalBurden", header: "Thermal" },
            {
              key: "promotionState",
              header: "Promotion state",
              render: (row) => <StatusPill tone="red">{row.promotionState}</StatusPill>,
            },
            {
              key: "evidenceHash",
              header: "Evidence hash",
              render: (row) => <code>{row.evidenceHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>
    </section>
  );
}
